<template>
	<v-row>
		<v-col>

			<v-form
					v-model="formIsValid.template"
					ref="passTemplateForm"
					class="mb-8"
					@submit.prevent="saveChanges"
			>

				<ui-box
						:form="$refs.passTemplateForm"
						:eager="!!localPass?.template_id"
						new-forms
				>
					<!-- Product Requirements -->
					<form-title title="Pass Type">
						Your pass type will help determine some initial settings for your
						pass and make it faster to build and put into the wild! You may choose
						to add other requirements when determining 'Things To Do.'
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<!-- Don't delete. This hidden field is required to track template changes because v-select is created and destroyed multiple times. -->
							<v-text-field
									v-if="!!localPass"
									v-model="localPass.template_id"
									v-show="false"
							></v-text-field>
							<template v-if="shouldShowSelectTemplate">
								<v-select
										v-if="!!localPass"
										v-model="localPass.template_id"
										class="required rounded-lg"
										clearable
										item-text="name"
										item-value="id"
										:items="passTemplates"
										hint="Warning: You will not be able to change this pass type once you save this page."
										label="Pass Type"
										outlined
										persistent-hint
										:rules="rules.template"
										@change="setCurrentPassTemplate"
								></v-select>
							</template>
							<template v-else>
								<v-row>
									<v-col>
										<v-text-field
												class="rounded-lg"
												clearable
												disabled
												hide-details="auto"
												label="Pass Type"
												outlined
												:value="currentPassTemplate?.name"
												:rules="rules.template"
										></v-text-field>
									</v-col>
									<!--
									<v-col cols="auto">
										<v-btn
												class="rounded-lg"
												color="grey darken-1"
												depressed
												outlined
												x-large
												@click="dialog.confirmPassTypeChange = true"
										>
											Switch
										</v-btn>
									</v-col>
									-->
								</v-row>
							</template>
						</v-col>
					</v-row>
				</ui-box>

			</v-form>

			<!-- Check-Ins -->
			<form-divider v-if="!hideCheckIns"/>

			<v-form
					v-model="formIsValid.checkIns"
					ref="checkInsForm"
					class="mb-8"
			>
				<ui-box
						v-if="!hideCheckIns"
						:eager="!!localPass?.template_id"
						:form="$refs.checkInsForm"
						new-forms
				>
					<form-title title="Default Check-In Method">
						<template v-if="!hasDealCheckInOverride">
							Your <span class="font-weight-bold">{{ currentPassTemplate?.name }}</span>
							will contain check-ins to your places. Here you can set the default
							validation type for places that will be onboarded.
						</template>
						<template v-else>
							Your <span class="font-weight-bold">{{ currentPassTemplate?.name }}</span>
							will contain <span class="font-weight-bold">{{ dealCheckInType }}</span>
							check-ins to your places. This validation method has been set as an
							override on the pass level.
						</template>
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols=“12” md="6">
							<v-select
									v-if="!hasDealCheckInOverride"
									v-model="localPass.pass_settings.default_checkin_template_id"
									class="rounded-lg required"
									hide-details="auto"
									:items="checkInTemplates"
									item-text="name"
									item-value="id"
									label="Check-in Validation"
									outlined
									:rules="rules.checkIn.validation"
							></v-select>
							<v-text-field
									v-else
									class="rounded-lg"
									disabled
									hide-details="auto"
									label="Check-in Validation"
									outlined
									readonly
									:value="dealCheckInType"
							></v-text-field>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider v-if="!hideCheckIns"/>

			<v-form v-model="formIsValid.repeat" ref="repeatForm" class="mb-8">

				<ui-box
					v-if="!hideCheckIns"
					:form="$refs.repeatForm"
					:eager="!!localPass.id"
					new-forms
				>

					<form-title title="Default Repeat Usage">
						<template>
							Want to invite your customers to visit places on your pass more than once? You can set a default frequency here to allow repeat check-ins on your pass.
						</template>
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" md="5" lg="4">
							<v-switch
								v-model="localPass.pass_settings.default_repeat"
								:class="localPass.pass_settings.default_repeat ? 'mt-3' : 'mb-2 mt-0'"
								dense
								hide-details="auto"
								inset
								label="Set default repeat usage?"
							></v-switch>
						</v-col>
						<v-col cols="12" md="7" lg="8">
							<v-row v-if="localPass.pass_settings.default_repeat">
								<v-col>
									<v-text-field
										v-model.number="localPass.pass_settings.default_repeat_delay"
										class="flex-grow-1 rounded-lg required"
										clearable
										hide-spin-buttons
										label="Repeat Delay"
										min="1"
										outlined
										prefix="Every"
										:rules="rules.repeat_delay"
										type="number"
										@wheel.native.prevent
									></v-text-field>
								</v-col>
								<v-col>
									<v-select
										v-model="localPass.pass_settings.default_repeat_delay_type"
										class="flex-grow-1 rounded-lg required"
										clearable
										:items="['hours', 'days', 'weeks', 'months']"
										label="Repeat Delay Type"
										outlined
										:rules="rules.repeat_delay_type"
									></v-select>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</ui-box>

			</v-form>

			<form-divider/>
			
			<v-form
					v-model="formIsValid.dates"
					ref="datesForm"
					class="mb-8"
			>
				<ui-box
						:eager="!!localPass?.template_id"
						:form="$refs.datesForm"
						new-forms
				>
					<form-title title="Anticipated Launch Date">
						This is the date that you plan to launch the pass.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols=“12” md="6">
							<bw-date-time-picker
									class="rounded-lg"
									v-if="localPass"
									v-model="localPass.launch_date"
									label="Anticipated Launch Date"
									required
									:rules="rules.launch_date"
							></bw-date-time-picker>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form
					v-model="formIsValid.name"
					ref="nameForm"
					class="mb-8"
					@submit.prevent="saveChanges"
			>

				<ui-box
						:form="$refs.nameForm"
						:eager="!!pass.id"
						new-forms
				>
					<form-title title="Pass Name">
						Let's get started by giving your pass a strong name that gives your
						prospective audience an idea of what they should expect.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<v-text-field
									v-if="!!localPass"
									v-model="localPass.name"
									class="rounded-lg required"
									clearable
									hint='Ex: 1 Day Connect Pass'
									label="Pass Name"
									outlined
									persistent-hint
									:rules="rules.name"
									type="text"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Name -->
				</ui-box>

			</v-form>

			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					save-text="Save"
					@cancel-action="cancelChanges"
					@save-action="saveChanges"
			></save-form>

			<!-- TODO: Make component? -->
			<v-dialog
					v-model="dialog.confirmPassTypeChange"
					content-class="rounded-xl"
					persistent
					scrollable
					width="600px"
			>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>Change Pass Type</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">

						<v-row>
							<v-col>
								We get it, changing pass types is sometimes necessary. You will need to
								create new conditions and gather any inventory again for the new pass
								type.
							</v-col>
						</v-row>

					</v-card-text>

					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<span></span>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="dialog.confirmPassTypeChange = false"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										@click="clearPassType"
								>
									Confirm
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-dialog>

		</v-col>
	</v-row>
</template>

<script>
import {passHelpers} from '../../helpers';

export default {
	name: 'PassBasicsName',
	props: {
		// The incoming deal from an edit
		checkInTemplates: {
			type: Array,
			default: () => []
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object | null,
			default: null
		},
		passTemplates: {
			type: Array,
			default: () => []
		},
		returnUrl: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			currentPassTemplate: null,
			dialog: {
				confirmPassTypeChange: false
			},
			formIsValid: {
				template: false,
				name: false
			},
			isSaving: false,
			localPass: null,
			rules: {
				checkIn: {
					validation: [
						v => !!v || 'Check-in validation type is required'
					]
				},
				launch_date: [v => !!v || 'Launch date is required.'],
				name: [v => !!v || 'Name is required.'],
				template: [v => !!v || 'Type is required.'],
				repeat_delay: [
					v => !!v || 'Repeat delay is required.',
					v => !isNaN(v) && v > 0 || 'Repeat delay must be greater than 0.'
				],
				repeat_delay_type: [
					v => !!v || 'Repeat delay type is required.'
				]
			},
		};
	},
	created() {
		this.localPass = {
			...cloneDeep(this.pass),
			highlights: this.pass.highlights || passHelpers.getPassHighlights(),
		};
		const { template_id } = this.localPass;
		if(template_id)
			this.currentPassTemplate = this.passTemplates.find(t => t.id === template_id);
		this.$set(this.localPass, 'pass_settings', {
			...(this.localPass?.pass_settings || {})
		});
	},
	computed: {
		dealCheckInType() {
			const { checkInTemplates, localPass } = this;

			if([null, '', 'by_product']
					.includes(localPass?.check_in_validation ?? null)) return null;
			return checkInTemplates
					.find(template => template.redemption_validation === localPass.check_in_validation)
					.name;
		},
		hasDealCheckInOverride() {
			return ![null, 'by_product'].includes(this.localPass?.check_in_validation ?? null);
		},
		hideCheckIns() {
			return !(this.currentPassTemplate?.settings?.places_to_check_in?.hide === false)
					&& !(this.currentPassTemplate?.settings?.force_checkin_validation === true);
		},
		shouldShowSelectTemplate() {
			return !this.localPass?.id || this.localPass?.id && !this.localPass?.template_id;
		}
	},
	methods: {
		cancelChanges() {
			window.location = this.getReturnUrl();
		},
		clearPassType() {
			this.currentPassTemplate = null;
			this.localPass.template_id = null;
			this.dialog.confirmPassTypeChange = false;
		},
		getReturnUrl() {
			return passHelpers.getReturnUrl(this.returnUrl, this.partner);
		},

		async saveChanges() {
			// const { formIsValid, localPass, partner, $refs } = this;
			// $refs.passTemplateForm.validate();
			// $refs.nameForm.validate();
			// if(!formIsValid.template || !formIsValid.name) return;

			this.isSaving = true;
			const url = `/partner-portal/${this.partner.id}/passes/save-pass${this.localPass.id
					? `/${this.localPass.id}`
					: ''}`;

			try {
				const { data } = await axios.put(url, this.localPass);
				window.location = `/partner-portal/${this.partner.id}/passes/${data.pass.id}/details`;
			}
			catch (error) {
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
		},
		setCurrentPassTemplate(passTemplateId) {
			this.currentPassTemplate = this.passTemplates.find(template => template.id === passTemplateId);
		}
	}
};

</script>
